/**
 * Holiday Constants
 * Shared constants for holiday detection across the application.
 * Used by both holidays.ts (Google Calendar) and holidays-simple.ts (Supabase).
 */

/**
 * Map of country name variations to ISO country codes.
 * Includes native language names and common variations.
 */
export const COUNTRY_PATTERNS: Record<string, string> = {
  // Major Non-EU Countries
  'United States': 'US',
  'USA': 'US',
  'U.S.A': 'US',
  'United Kingdom': 'GB',
  'UK': 'GB',
  'Great Britain': 'GB',
  'England': 'GB',
  'Scotland': 'GB',
  'Wales': 'GB',
  'Canada': 'CA',
  'Australia': 'AU',
  'New Zealand': 'NZ',
  'Japan': 'JP',
  '日本': 'JP',
  'China': 'CN',
  '中国': 'CN',
  'India': 'IN',
  'भारत': 'IN',
  'Brazil': 'BR',
  'Brasil': 'BR',
  'Mexico': 'MX',
  'México': 'MX',
  'Argentina': 'AR',
  'South Africa': 'ZA',
  'Russia': 'RU',
  'Россия': 'RU',
  'South Korea': 'KR',
  '대한민국': 'KR',
  'Thailand': 'TH',
  'ไทย': 'TH',
  'Singapore': 'SG',
  'Malaysia': 'MY',
  'Indonesia': 'ID',
  'Philippines': 'PH',
  'Vietnam': 'VN',
  'Việt Nam': 'VN',
  'Switzerland': 'CH',
  'Schweiz': 'CH',
  'Suisse': 'CH',
  'Svizzera': 'CH',
  'Norway': 'NO',
  'Norge': 'NO',

  // European Union Countries (27 members)
  'Austria': 'AT',
  'Österreich': 'AT',
  'Belgium': 'BE',
  'België': 'BE',
  'Belgique': 'BE',
  'Bulgaria': 'BG',
  'България': 'BG',
  'Croatia': 'HR',
  'Hrvatska': 'HR',
  'Cyprus': 'CY',
  'Κύπρος': 'CY',
  'Czech Republic': 'CZ',
  'Czechia': 'CZ',
  'Česká republika': 'CZ',
  'Denmark': 'DK',
  'Danmark': 'DK',
  'Estonia': 'EE',
  'Eesti': 'EE',
  'Finland': 'FI',
  'Suomi': 'FI',
  'France': 'FR',
  'République française': 'FR',
  'Germany': 'DE',
  'Deutschland': 'DE',
  'Greece': 'GR',
  'Ελλάδα': 'GR',
  'Hungary': 'HU',
  'Magyarország': 'HU',
  'Ireland': 'IE',
  'Éire': 'IE',
  'Italy': 'IT',
  'Italia': 'IT',
  'Latvia': 'LV',
  'Latvija': 'LV',
  'Lithuania': 'LT',
  'Lietuva': 'LT',
  'Luxembourg': 'LU',
  'Lëtzebuerg': 'LU',
  'Malta': 'MT',
  'Netherlands': 'NL',
  'Nederland': 'NL',
  'Poland': 'PL',
  'Polska': 'PL',
  'Portugal': 'PT',
  'República Portuguesa': 'PT',
  'Romania': 'RO',
  'România': 'RO',
  'Slovakia': 'SK',
  'Slovensko': 'SK',
  'Slovenia': 'SI',
  'Slovenija': 'SI',
  'Spain': 'ES',
  'España': 'ES',
  'Sweden': 'SE',
  'Sverige': 'SE',
};

/**
 * Set of supported country codes for holiday detection.
 */
export const SUPPORTED_COUNTRIES = new Set([
  'US', 'GB', 'CA', 'AU', 'NZ', 'JP', 'CN', 'IN',
  'DE', 'FR', 'ES', 'IT', 'NL', 'BE', 'AT', 'CH',
  'SE', 'NO', 'DK', 'FI', 'PL', 'CZ', 'SK', 'HU',
  'RO', 'BG', 'HR', 'SI', 'LT', 'LV', 'EE', 'IE',
  'PT', 'GR', 'CY', 'MT', 'LU', 'BR', 'MX', 'AR',
  'ZA', 'RU', 'KR', 'TH', 'SG', 'MY', 'ID', 'PH', 'VN'
]);

/**
 * Google Calendar Holiday Calendar IDs by country.
 * These are public Google holiday calendars.
 */
export const GOOGLE_HOLIDAY_CALENDARS: Record<string, string> = {
  // Major Non-EU Countries
  'US': 'en.usa#holiday@group.v.calendar.google.com',
  'GB': 'en.uk#holiday@group.v.calendar.google.com',
  'CA': 'en.canadian#holiday@group.v.calendar.google.com',
  'AU': 'en.australian#holiday@group.v.calendar.google.com',
  'NZ': 'en.new_zealand#holiday@group.v.calendar.google.com',
  'JP': 'en.japanese#holiday@group.v.calendar.google.com',
  'CN': 'en.china#holiday@group.v.calendar.google.com',
  'IN': 'en.indian#holiday@group.v.calendar.google.com',
  'BR': 'en.brazilian#holiday@group.v.calendar.google.com',
  'MX': 'en.mexican#holiday@group.v.calendar.google.com',
  'AR': 'en.ar#holiday@group.v.calendar.google.com',
  'ZA': 'en.sa#holiday@group.v.calendar.google.com',
  'RU': 'en.russian#holiday@group.v.calendar.google.com',
  'KR': 'en.south_korea#holiday@group.v.calendar.google.com',
  'TH': 'en.th#holiday@group.v.calendar.google.com',
  'SG': 'en.singapore#holiday@group.v.calendar.google.com',
  'MY': 'en.malaysia#holiday@group.v.calendar.google.com',
  'ID': 'en.indonesia#holiday@group.v.calendar.google.com',
  'PH': 'en.philippines#holiday@group.v.calendar.google.com',
  'VN': 'en.vietnam#holiday@group.v.calendar.google.com',
  'CH': 'en.ch#holiday@group.v.calendar.google.com',
  'NO': 'en.norwegian#holiday@group.v.calendar.google.com',

  // European Union Countries (27 members)
  'AT': 'en.austrian#holiday@group.v.calendar.google.com',
  'BE': 'en.be#holiday@group.v.calendar.google.com',
  'BG': 'en.bulgarian#holiday@group.v.calendar.google.com',
  'HR': 'en.croatian#holiday@group.v.calendar.google.com',
  'CY': 'en.cyprus#holiday@group.v.calendar.google.com',
  'CZ': 'en.czech#holiday@group.v.calendar.google.com',
  'DK': 'en.danish#holiday@group.v.calendar.google.com',
  'EE': 'en.estonian#holiday@group.v.calendar.google.com',
  'FI': 'en.finnish#holiday@group.v.calendar.google.com',
  'FR': 'en.french#holiday@group.v.calendar.google.com',
  'DE': 'en.german#holiday@group.v.calendar.google.com',
  'GR': 'en.greek#holiday@group.v.calendar.google.com',
  'HU': 'en.hungarian#holiday@group.v.calendar.google.com',
  'IE': 'en.irish#holiday@group.v.calendar.google.com',
  'IT': 'en.italian#holiday@group.v.calendar.google.com',
  'LV': 'en.latvian#holiday@group.v.calendar.google.com',
  'LT': 'en.lithuanian#holiday@group.v.calendar.google.com',
  'LU': 'en.luxembourg#holiday@group.v.calendar.google.com',
  'MT': 'en.malta#holiday@group.v.calendar.google.com',
  'NL': 'en.dutch#holiday@group.v.calendar.google.com',
  'PL': 'en.polish#holiday@group.v.calendar.google.com',
  'PT': 'en.portuguese#holiday@group.v.calendar.google.com',
  'RO': 'en.romanian#holiday@group.v.calendar.google.com',
  'SK': 'en.slovak#holiday@group.v.calendar.google.com',
  'SI': 'en.slovenian#holiday@group.v.calendar.google.com',
  'ES': 'en.spain#holiday@group.v.calendar.google.com',
  'SE': 'en.swedish#holiday@group.v.calendar.google.com',
};

/**
 * Extract country code from an address string.
 * Searches for country name patterns in the address.
 */
export function extractCountryFromAddress(address: string): string | null {
  if (!address) return null;

  // Check for country patterns in the address
  for (const [pattern, code] of Object.entries(COUNTRY_PATTERNS)) {
    if (address.includes(pattern)) {
      return code;
    }
  }

  // Try to extract from the last part of the address (usually the country)
  const parts = address.split(',').map(p => p.trim());
  const lastPart = parts[parts.length - 1];

  for (const [pattern, code] of Object.entries(COUNTRY_PATTERNS)) {
    if (lastPart.toLowerCase() === pattern.toLowerCase()) {
      return code;
    }
  }

  return null;
}

/**
 * Check if a country code is supported for holiday detection.
 */
export function isCountrySupported(countryCode: string): boolean {
  return SUPPORTED_COUNTRIES.has(countryCode);
}

/**
 * Get country name from country code.
 */
export function getCountryName(countryCode: string): string {
  // Reverse lookup - find first English name for the country code
  for (const [name, code] of Object.entries(COUNTRY_PATTERNS)) {
    if (code === countryCode && /^[A-Za-z\s]+$/.test(name)) {
      return name;
    }
  }
  return countryCode;
}
